/**
 * 事件配置编辑器
 * 支持系统事件、剧情事件、触发器配置
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  Alert,
  message,
} from 'antd'
import {
  BellOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  FlashOutlined,
} from '@ant-design/icons'

const { TextArea } = Input
const { Text, Title } = Typography

// 事件类型选项
const EVENT_TYPE_OPTIONS = [
  { value: 'system', label: '系统事件', color: 'blue' },
  { value: 'story', label: '剧情事件', color: 'purple' },
  { value: 'combat', label: '战斗事件', color: 'red' },
  { value: 'environment', label: '环境事件', color: 'green' },
  { value: 'special', label: '特殊事件', color: 'gold' },
]

// 触发类型选项
const EVENT_TRIGGER_OPTIONS = [
  { value: 'time', label: '时间触发' },
  { value: 'condition', label: '条件触发' },
  { value: 'action', label: '动作触发' },
  { value: 'npc', label: 'NPC触发' },
  { value: 'location', label: '地点触发' },
  { value: 'variable', label: '变量触发' },
]

interface EventEditorFormProps {
  initialData?: Record<string, any>
  onSave: (data: Record<string, any>) => Promise<void>
  loading?: boolean
}

const EventEditorForm: React.FC<EventEditorFormProps> = ({
  initialData,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key || initialData.id,
        name: initialData.name,
        description: initialData.description,
        event_type: initialData.type,
        trigger_type: initialData.trigger,
        priority: initialData.priority,
        repeatable: initialData.repeatable,
      })
    } else {
      form.resetFields()
    }
  }, [initialData, form])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const eventData = {
        id: values.key || values.id,
        key: values.key,
        name: values.name,
        description: values.description,
        type: values.event_type,
        trigger: values.trigger_type,
        priority: values.priority,
        repeatable: values.repeatable,
        conditions: initialData?.conditions || {},
        effects: initialData?.effects || [],
      }

      await onSave(eventData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <BellOutlined style={{ color: '#fa8c16' }} />
          <span>{initialData?.name || '新建事件'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="事件键" rules={[{ required: true }]}>
                  <Input placeholder="例如: event_rain_start" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="事件名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 大雨开始" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="event_type" label="事件类型" rules={[{ required: true }]}>
                  <Select placeholder="选择事件类型" options={EVENT_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="描述">
              <TextArea rows={2} placeholder="事件的详细说明..." />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="trigger_type" label="触发类型">
                  <Select placeholder="选择触发类型" options={EVENT_TRIGGER_OPTIONS} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="priority" label="优先级">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="50" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="repeatable" label="可重复触发" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="触发条件" key="conditions">
            <Alert
              message="触发条件配置"
              description="配置事件触发的条件，满足条件时事件才会执行。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="conditions" label="触发条件 (JSON)">
              <TextArea
                rows={10}
                placeholder={`{
  "time": {
    "start": 1800,
    "end": 3600,
    "repeat": false
  },
  "variable": {
    "var_weather": "clear",
    "var_alert_level": {"min": 50}
  },
  "player": {
    "location": "loc_hall",
    "count": {"min": 2, "max": 6}
  },
  "npc": {
    "present": ["npc_mysterious_man"],
    "alive": true
  }
}`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="执行效果" key="effects">
            <Alert
              message="执行效果配置"
              description="事件触发后执行的效果，如修改变量、发送广播、播放视频等。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="effects" label="执行效果 (JSON)">
              <TextArea
                rows={12}
                placeholder={`[
  {
    "action": "modify_variable",
    "variable": "var_weather",
    "value": "rain",
    "duration": 240
  },
  {
    "action": "broadcast",
    "message": "突然下起了大雨！",
    "type": "warning"
  },
  {
    "action": "play_music",
    "music": "rain_ambience",
    "fade_in": 2000
  },
  {
    "action": "modify_attribute",
    "target": "all_players",
    "attribute": "visibility",
    "modifier": -30
  }
]`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="冷却配置" key="cooldown">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['cooldown', 'enabled']} label="启用冷却" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['cooldown', 'duration']} label="冷却时间 (秒)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="300" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['cooldown', 'max_uses']} label="最大使用次数">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Alert
              message="重置配置"
              description="配置冷却时间的重置方式。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name={['cooldown', 'reset_on']} label="重置条件">
              <Select
                mode="tags"
                placeholder="act_end, game_restart, manual"
                style={{ width: '100%' }}
              >
                <Option value="act_end">幕结束</Option>
                <Option value="game_restart">游戏重置</Option>
                <Option value="manual">手动重置</Option>
              </Select>
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs
const { Option } = Select

export default EventEditorForm
